import React, { createContext, useContext, useRef, useState, useMemo, useEffect } from "react";
import { OnboardingState } from "@/types/onbaording.types";
import { safeUrlDecode, cleanAndNormaliseUrl } from "@/utils/urlHelpers";

type OnboardingContextType = {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  likedIdeas: string[];
  setLikedIdeas: React.Dispatch<React.SetStateAction<string[]>>;
  expandedSteps: Record<number, boolean>;
  setExpandedSteps: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  stepTexts: Record<number, string>;
  setStepTexts: React.Dispatch<React.SetStateAction<Record<number, string>>>;
  setupStarted: React.MutableRefObject<boolean>;
  selectedIntegrations: { [key: string]: string[] };
  setSelectedIntegrations: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Derive initial websiteUrl synchronously from URL (?url or #domain)
  const initialWebsiteUrl = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlParam = params.get("url");
      const hashValue = (window.location.hash || "").replace(/^#/, "").trim();
      
      console.log('OnboardingContext: Processing URL parameters:', { urlParam, hashValue });
      
      // Check if we have a URL parameter first (preferred method)
      if (urlParam) {
        const formatted = safeUrlDecode(urlParam);
        console.log('OnboardingContext: Using URL parameter:', formatted);
        return formatted;
      }
      
      // Handle external WordPress redirects with encoded URLs in hash fragments
      if (hashValue && hashValue.includes('%')) {
        try {
          // Decode the hash fragment - this handles external WordPress redirects
          const decodedHash = decodeURIComponent(hashValue);
          console.log('OnboardingContext: Decoded external WordPress redirect:', decodedHash);
          
          // Check if the decoded hash looks like a URL
          if (decodedHash.includes('://') || decodedHash.includes('.')) {
            const formatted = cleanAndNormaliseUrl(decodedHash);
            console.log('OnboardingContext: Using external WordPress URL:', formatted);
            return formatted;
          }
        } catch (e) {
          console.warn('Failed to decode external WordPress redirect:', e);
        }
      }
      
      // Check if hash fragment is a plain domain (fallback)
      if (hashValue && !hashValue.includes('%') && hashValue.includes('.')) {
        const formatted = cleanAndNormaliseUrl(hashValue);
        console.log('OnboardingContext: Using hash fragment as domain:', formatted);
        return formatted;
      }
      
    } catch (e) {
      console.warn('Error processing URL parameter:', e);
    }
    return localStorage.getItem("onboardingWebsite") || "https://example.com";
  }, []); // Empty dependency array ensures this only runs once

  const [state, setState] = useState<OnboardingState>({
    step: 1,
    websiteUrl: initialWebsiteUrl,
    progress: 0,
    currentStepIndex: 0,
    currentStepText: "",
    selectedType: null,
    contentGenerated: false,
    generatedContentTitle: "",
    generatedContentPreview: "",
    showRawContent: false,
    showWpAuthDialog: false,
    wpUsername: "",
    wpPassword: "",
    isAuthenticating: false,
    wpConnectionError: null,
    postingFrequency: 3,
    postingDays: ["monday", "wednesday", "friday"],
    scheduledDates: [],
    showSignupModal: false,
    showEmailVerification: false,
  });

  const [likedIdeas, setLikedIdeas] = useState<string[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  const [stepTexts, setStepTexts] = useState<Record<number, string>>({});
  const [selectedIntegrations, setSelectedIntegrations] = useState<{ [key: string]: string[] }>({});
  const setupStarted = useRef(false);

  // Save websiteUrl to localStorage when it changes (but not during initial setup)
  useEffect(() => {
    if (state.websiteUrl && state.websiteUrl !== "https://example.com") {
      localStorage.setItem("onboardingWebsite", state.websiteUrl);
    }
  }, [state.websiteUrl]);

  return (
    <OnboardingContext.Provider
      value={{
        state,
        setState,
        likedIdeas,
        setLikedIdeas,
        expandedSteps,
        setExpandedSteps,
        stepTexts,
        setStepTexts,
        setupStarted,
        selectedIntegrations,
        setSelectedIntegrations,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error("useOnboarding must be used within OnboardingProvider");
  return ctx;
};
